import React from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { VeraPromptProposal } from '../../../models/admin';
import { StyledButton } from '../../styles/StyledButtons';
import VeraPromptDiffView from './VeraPromptDiffView';
import { formatProposalChipLabel } from './veraGlobalChatPromptOverrideUtils';

interface InvestorProposalLinkBannerProps {
  proposalId: string | undefined;
  linkedProposalLoading: boolean;
  linkedProposal: VeraPromptProposal | null;
  onCopyToEditor: () => void;
}

export default function InvestorProposalLinkBanner({
  proposalId,
  linkedProposalLoading,
  linkedProposal,
  onCopyToEditor,
}: InvestorProposalLinkBannerProps) {
  if (!proposalId) return null;

  if (linkedProposalLoading) {
    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading proposal…</Typography>
      </Box>
    );
  }

  if (!linkedProposal) return null;

  const isAdminAssist =
    (linkedProposal.changedPrompts ?? []).includes('admin_assist') ||
    linkedProposal.promptType === 'admin_assist';

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        border: '1px solid',
        borderColor: 'info.main',
        borderRadius: 1,
        background: 'rgba(2,136,209,0.06)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2">
          Proposal <strong>{linkedProposal.externalId}</strong>
          {linkedProposal.createdAt && (
            <> — {new Date(linkedProposal.createdAt).toLocaleString()}</>
          )}
        </Typography>
        {(linkedProposal.changedPrompts ?? []).map((promptKey) => (
          <Chip
            key={promptKey}
            label={formatProposalChipLabel(promptKey)}
            size="small"
            color={
              promptKey === 'system' ? 'primary' : promptKey === 'intent' ? 'secondary' : 'warning'
            }
            variant="outlined"
          />
        ))}
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Read-only proposal view. Copy to editor to create a new version.
      </Typography>

      {linkedProposal.systemMarkdown && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
            {isAdminAssist ? 'Vera Admin Assist Prompt (proposed)' : 'System Prompt (proposed)'}
          </Typography>
          {linkedProposal.systemDiff ? (
            <VeraPromptDiffView diff={linkedProposal.systemDiff} />
          ) : (
            <Box
              component="pre"
              sx={{
                p: 1.5,
                background: '#f5f5f5',
                borderRadius: 1,
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              {linkedProposal.systemMarkdown}
            </Box>
          )}
        </Box>
      )}

      {linkedProposal.intentText && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
            Intent Prompt (proposed)
          </Typography>
          {linkedProposal.intentDiff ? (
            <VeraPromptDiffView diff={linkedProposal.intentDiff} />
          ) : (
            <Box
              component="pre"
              sx={{
                p: 1.5,
                background: '#f5f5f5',
                borderRadius: 1,
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {linkedProposal.intentText}
            </Box>
          )}
        </Box>
      )}

      {linkedProposal.systemMarkdown && (
        <StyledButton size="small" onClick={onCopyToEditor}>
          Copy system prompt to editor
        </StyledButton>
      )}
    </Box>
  );
}
